import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  MapPin, Globe, Clock, MessageCircle, Search, 
  Filter, ChevronLeft, Loader2, Send, UserPlus, X
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BrowsePenPalsPage() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const [filters, setFilters] = useState({
    country: "",
    writing_frequency: "",
    language: "",
    interest: ""
  });

  useEffect(() => {
    fetchProfiles();
  }, [filters]);

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filters.country) params.append("country", filters.country);
      if (filters.writing_frequency) params.append("writing_frequency", filters.writing_frequency);
      if (filters.language) params.append("language", filters.language);
      if (filters.interest) params.append("interest", filters.interest);
      
      const { data } = await axios.get(`${BACKEND_URL}/api/penpal/browse?${params}`, { withCredentials: true });
      setProfiles(data.profiles);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (profileId) => {
    setSending(true);
    setError(null);
    
    try {
      await axios.post(`${BACKEND_URL}/api/penpal/connect`, {
        to_profile_id: profileId,
        message: connectionMessage
      }, { withCredentials: true });
      
      setSuccessMessage("Connection request sent!");
      setSelectedProfile(null);
      setConnectionMessage("");
      
      // Refresh profiles to update connection status
      setTimeout(() => {
        setSuccessMessage(null);
        fetchProfiles();
      }, 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const clearFilters = () => {
    setFilters({ country: "", writing_frequency: "", language: "", interest: "" });
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in to browse pen pals</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (!user.penpal_profile_id) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Create a profile to browse pen pals</h2>
          <p className="text-lws-taupe mb-8">You need to create your pen pal profile before you can browse and connect with others.</p>
          <Button href="#penpal-create">Create Profile</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-browse">
      <div className="max-w-6xl mx-auto" data-testid="browse-page">
        <a href="#penpal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Pen Pals
        </a>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-serif text-lws-green">Find Your Pen Pal</h1>
            <p className="text-lws-taupe mt-2">Browse profiles and send connection requests</p>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-lws-ink/20 hover:border-lws-green transition-colors"
          >
            <Filter size={18} />
            Filters
            {Object.values(filters).some(v => v) && (
              <span className="w-2 h-2 rounded-full bg-lws-amber" />
            )}
          </button>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="bg-lws-taupe/5 p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <div>
              <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Country</label>
              <input
                type="text"
                value={filters.country}
                onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                placeholder="e.g., Australia"
                className="w-full border border-lws-ink/10 px-3 py-2 text-sm focus:outline-none focus:border-lws-green"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Writing Frequency</label>
              <select
                value={filters.writing_frequency}
                onChange={(e) => setFilters(prev => ({ ...prev, writing_frequency: e.target.value }))}
                className="w-full border border-lws-ink/10 px-3 py-2 text-sm focus:outline-none focus:border-lws-green bg-white"
              >
                <option value="">Any</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="occasional">Occasional</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Language</label>
              <input
                type="text"
                value={filters.language}
                onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value }))}
                placeholder="e.g., English"
                className="w-full border border-lws-ink/10 px-3 py-2 text-sm focus:outline-none focus:border-lws-green"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-lws-taupe mb-2">Interest</label>
              <input
                type="text"
                value={filters.interest}
                onChange={(e) => setFilters(prev => ({ ...prev, interest: e.target.value }))}
                placeholder="e.g., Literature"
                className="w-full border border-lws-ink/10 px-3 py-2 text-sm focus:outline-none focus:border-lws-green"
              />
            </div>
            <div className="md:col-span-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-lws-taupe hover:text-lws-green transition-colors"
              >
                Clear all filters
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Success Message */}
        {successMessage && (
          <div className="bg-lws-green/10 text-lws-green p-4 mb-8 text-center">
            {successMessage}
          </div>
        )}
        
        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-8 text-center">
            {error}
          </div>
        )}
        
        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-lws-green" />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-lws-taupe mb-4">No profiles found</p>
            <p className="text-sm text-lws-taupe">Try adjusting your filters or check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="profiles-grid">
            {profiles.map((profile) => (
              <motion.div
                key={profile.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-lws-ink/5 p-6 hover:border-lws-amber/30 transition-all cursor-pointer"
                onClick={() => setSelectedProfile(profile)}
                data-testid={`profile-card-${profile.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-serif">{profile.display_name}</h3>
                    <p className="text-sm text-lws-taupe flex items-center gap-1 mt-1">
                      <MapPin size={14} /> {profile.country}
                    </p>
                  </div>
                  {profile.connection_status && (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                      profile.connection_status === 'matched' ? 'bg-lws-green text-lws-paper' :
                      profile.connection_status === 'pending' ? 'bg-lws-amber/20 text-lws-amber' :
                      'bg-lws-taupe/10 text-lws-taupe'
                    }`}>
                      {profile.connection_status}
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-lws-taupe line-clamp-3 mb-4">{profile.bio}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {profile.interests.slice(0, 3).map((interest, idx) => (
                    <span key={idx} className="text-[10px] uppercase tracking-widest bg-lws-amber/10 px-2 py-1">
                      {interest}
                    </span>
                  ))}
                  {profile.interests.length > 3 && (
                    <span className="text-[10px] text-lws-taupe">+{profile.interests.length - 3} more</span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-lws-taupe border-t border-lws-ink/5 pt-4">
                  <span className="flex items-center gap-1">
                    <Clock size={12} /> {profile.writing_frequency}
                  </span>
                  <span className="flex items-center gap-1">
                    <Globe size={12} /> {profile.languages.join(", ")}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Profile Detail Modal */}
        {selectedProfile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedProfile(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-lws-paper max-w-lg w-full max-h-[90vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
              data-testid="profile-modal"
            >
              <button
                onClick={() => setSelectedProfile(null)}
                className="absolute top-4 right-4 text-lws-taupe hover:text-lws-ink"
              >
                <X size={24} />
              </button>
              
              <h2 className="text-3xl font-serif text-lws-green mb-2">{selectedProfile.display_name}</h2>
              <p className="text-sm text-lws-taupe flex items-center gap-1 mb-6">
                <MapPin size={14} /> {selectedProfile.country}
              </p>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">About</h4>
                  <p className="text-lws-ink">{selectedProfile.bio}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">Writing Frequency</h4>
                    <p className="capitalize">{selectedProfile.writing_frequency.replace("_", " ")}</p>
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">Location Preference</h4>
                    <p className="capitalize">{selectedProfile.location_preference.replace("_", " ")}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">Languages</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.languages.map((lang, idx) => (
                      <span key={idx} className="bg-lws-taupe/10 px-3 py-1 text-sm">{lang}</span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">Interests</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.interests.map((interest, idx) => (
                      <span key={idx} className="bg-lws-amber/20 px-3 py-1 text-sm">{interest}</span>
                    ))}
                  </div>
                </div>
                
                {!selectedProfile.connection_status && (
                  <div className="border-t border-lws-ink/5 pt-6">
                    <h4 className="text-xs uppercase tracking-widest text-lws-taupe mb-2">Send a Connection Request</h4>
                    <textarea
                      value={connectionMessage}
                      onChange={(e) => setConnectionMessage(e.target.value)}
                      placeholder="Introduce yourself... (optional)"
                      rows={3}
                      className="w-full border border-lws-ink/10 px-4 py-3 focus:outline-none focus:border-lws-green mb-4"
                    />
                    <button
                      onClick={() => handleConnect(selectedProfile.id)}
                      disabled={sending}
                      className="w-full bg-lws-green text-lws-paper py-3 text-xs uppercase tracking-widest font-medium hover:bg-lws-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="connect-btn"
                    >
                      {sending ? (
                        <><Loader2 size={16} className="animate-spin" /> Sending...</>
                      ) : (
                        <><Send size={16} /> Send Request</>
                      )}
                    </button>
                  </div>
                )}
                
                {selectedProfile.connection_status === 'pending' && (
                  <div className="bg-lws-amber/10 p-4 text-center">
                    <p className="text-sm text-lws-amber">Connection request pending</p>
                  </div>
                )}
                
                {selectedProfile.connection_status === 'matched' && (
                  <div className="bg-lws-green/10 p-4 text-center">
                    <p className="text-sm text-lws-green">You're matched! Check your connections to see their address.</p>
                    <a href="#penpal-connections" className="text-xs uppercase tracking-widest text-lws-green hover:underline mt-2 inline-block">
                      View Connections
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </Section>
  );
}
